import type {
  ChartComponentSpec,
  ChartSortOption,
  WidgetResponseSpec,
  ProcessedWidgetData,
  SubjectEntity,
  MetricOption,
} from "../../Processing/types";
import { processWidget } from "../../Processing/pipeline";
import { resolveControllerDisplay, type ControllerOption } from "../../Processing/controllerBindings";
import { resolveSubjectNames } from "../shared/PlayersForm";
import { resolveSpecBaselines } from "../../Processing/baselineResolution";
import { GRAPH_COLORS } from "../constants";
import type { GraphFormState, GroupByOption } from "./plotState";
import { formStateToSpec } from "./plotState";

const FALLBACK_CATEGORIES: Record<GroupByOption, string[]> = {
  day: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  week: ["Wk 1", "Wk 2", "Wk 3", "Wk 4"],
  month: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
  year: ["2023", "2024", "2025", "2026"],
  player: ["Player 1", "Player 2", "Player 3", "Player 4", "Player 5"],
  position: ["Position 1", "Position 2", "Position 3", "Position 4"],
  drill: ["Drill 1", "Drill 2", "Drill 3", "Drill 4"],
};

function seededRandom(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 49297;
  return x - Math.floor(x);
}

const ENTITY_GROUP_BY: Record<string, GroupByOption> = {
  player: "player",
  position: "position",
  drill: "drill",
};

export class PlotPreviewBuilder {
  private graphs: GraphFormState[];
  private metrics: MetricOption[];
  private plotTitle: string;
  private showLegend: boolean;
  private inverted: boolean;
  private showAllDatesInRange: boolean;
  private groupBy: GroupByOption;
  private sortBy: ChartSortOption | null;
  private sortSeriesId: string | null;
  private sortIncludeStacked: boolean;
  private getSubjectEntities: (subjectType: string) => SubjectEntity[];
  private controllers: ControllerOption[];
  private allPlayers: any[];

  constructor(
    graphs: GraphFormState[],
    metrics: MetricOption[],
    plotTitle: string,
    showLegend: boolean,
    inverted: boolean,
    showAllDatesInRange: boolean,
    groupBy: GroupByOption,
    sortBy: ChartSortOption | null,
    sortSeriesId: string | null,
    sortIncludeStacked: boolean,
    getSubjectEntities: (subjectType: string) => SubjectEntity[],
    allPlayers: any[] = [],
    controllers: ControllerOption[] = [],
  ) {
    this.graphs = graphs;
    this.metrics = metrics;
    this.plotTitle = plotTitle;
    this.showLegend = showLegend;
    this.inverted = inverted;
    this.showAllDatesInRange = showAllDatesInRange;
    this.groupBy = groupBy;
    this.sortBy = sortBy;
    this.sortSeriesId = sortSeriesId;
    this.sortIncludeStacked = sortIncludeStacked;
    this.getSubjectEntities = getSubjectEntities;
    this.allPlayers = allPlayers;
    this.controllers = controllers;
  }

  hasMetric(): boolean {
    return this.graphs.some((g) => g.selectedMetricId != null);
  }

  buildSpec(): ChartComponentSpec | null {
    return formStateToSpec(
      {
        title: this.plotTitle,
        showLegend: this.showLegend,
        inverted: this.inverted,
        showAllDatesInRange: this.showAllDatesInRange,
        groupBy: this.groupBy,
        sortBy: this.sortBy,
        sortSeriesId: this.sortSeriesId,
        sortIncludeStacked: this.sortIncludeStacked,
        graphs: this.graphs,
        expandedGraphKey: [],
      },
      this.metrics,
      this.getSubjectEntities,
    );
  }

  private resolveCategories(): string[] {
    const subjectType = ENTITY_GROUP_BY[this.groupBy];
    if (!subjectType) return FALLBACK_CATEGORIES[this.groupBy];

    const prefix = subjectType === "drill" ? "drill-" : subjectType === "position" ? "pos-" : "player-";
    const source = subjectType === "drill"
      ? this.graphs.flatMap((g) => g.selectedDrills)
      : this.graphs.flatMap((g) => g.selectedSubjects);

    const selectedIds = source
      .filter((v) => v.startsWith(prefix))
      .map((v) => v.slice(prefix.length));
    const uniqueIds = [...new Set(selectedIds)];

    if (uniqueIds.length === 0) return FALLBACK_CATEGORIES[this.groupBy];

    const entities = this.getSubjectEntities(subjectType);
    const idToName = new Map(entities.map((e) => [e.id, e.name]));
    return uniqueIds.map((id) => idToName.get(id) ?? id);
  }

  buildMockResponse(): WidgetResponseSpec {
    const categories = this.resolveCategories();
    const seriesData = this.graphs
      .filter((g) => g.selectedMetricId != null)
      .flatMap((g, i) => {
        const metric = this.metrics.find((m) => m.id === g.selectedMetricId);
        const base = 200 + i * 120;
        const mainSeries = {
          query_id: g.queryId,
          name: metric?.name?.toUpperCase() ?? `PLOT ${i + 1}`,
          data: categories.map((cat, ci) => ({
            value: Math.round(base + seededRandom(i * 1000 + ci) * 200),
            category: cat,
            hex: (g.enableColorOverride && g.colorOverride) || GRAPH_COLORS[i % GRAPH_COLORS.length],
          })),
        };

        if (g.showAcwr && g.selectedMetricId?.startsWith("gps-")) {
          const acwrSeries = {
            query_id: g.acwrQueryId,
            name: "ACWR",
            data: categories.map((cat, ci) => ({
              value: Math.round((0.5 + seededRandom(i * 2000 + ci)) * 100) / 100,
              category: cat,
              hex: "#1a73e8",
            })),
          };
          return [mainSeries, acwrSeries];
        }

        return [mainSeries];
      });

    return { widget_id: "preview", series: seriesData };
  }

  build(): ProcessedWidgetData | null {
    const spec = this.buildSpec();
    if (!spec) return null;
    // Resolve baseline reference lines against the real roster so the preview
    // shows the value the live widget will draw, not the value-0 placeholder.
    const resolvedSpec = resolveSpecBaselines(spec, this.allPlayers, this.metrics);
    const response = this.buildMockResponse();
    // The preview labels a bound plot with its controller's subjects/dates, the same way
    // the rendered widget does. The controller display wraps the baseline-resolved
    // spec, so the preview carries both the real reference lines and the bound labels.
    return processWidget(
      // Re-derive the controller's subject NAMES from its ids here too: the stored names
      // are a cache, and a stale one is what leaves the legend reading "Full Team".
      resolveControllerDisplay(resolvedSpec, this.controllers, (subjects, drills) =>
        resolveSubjectNames(subjects, drills, this.getSubjectEntities),
        this.getSubjectEntities,
      ),
      response,
    );
  }
}
